import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Breadcrumb, PageTitle, GradButton } from "../../components/loans/LoansUi";
import { Select, useSeo } from "../LoansLanding";
import { calcEmi, fmtINR } from "../../lib/loanStats";

const MIN = 50000, MAX = 5000000;
const RATES = [9.99, 10.5, 10.99, 11.25, 11.99, 12.5, 13.99, 15, 18, 21, 24];
const TENURES = [6, 12, 18, 24, 36, 48, 60];

function SummaryRow({ label, value, id }) {
  return (
    <div className="flex items-center justify-between py-5 border-b border-slate-200 text-lg">
      <span>{label}</span><span data-testid={`le-emi-${id}`} className="font-bold">{value}</span>
    </div>
  );
}

export default function LoansEmi() {
  useSeo("EMI Calculator · loanexpress", "Calculate your personal loan EMI, total interest and repayment amount.");
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState("11.25");
  const [tenure, setTenure] = useState("36");
  const [inputs, setInputs] = useState({ amount: 500000, rate: 11.25, tenure: 36 });
  const res = useMemo(() => calcEmi(inputs.amount, inputs.rate, inputs.tenure), [inputs]);
  const pct = ((Math.min(Math.max(amount, MIN), MAX) - MIN) / (MAX - MIN)) * 100;
  const calculate = () => { const a = Math.min(Math.max(amount, MIN), MAX); setAmount(a); setInputs({ amount: a, rate: Number(rate), tenure: Number(tenure) }); };

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-20">
      <Breadcrumb items={[{ label: "EMI Calculator" }]} />
      <div className="mt-10"><PageTitle plain="EMI" accent="Calculator" sub="Calculate your monthly EMI, total interest and repayment amount." /></div>
      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_-25px_rgba(15,40,90,0.2)] p-7 md:p-10">
          <label className="font-semibold">Loan Amount</label>
          <div className="mt-2 h-14 rounded-xl border border-slate-200 flex items-center px-4 focus-within:border-[#10B981] focus-within:ring-4 focus-within:ring-emerald-100">
            <span className="text-xl font-bold">₹</span>
            <input data-testid="le-emi-amount-input" inputMode="numeric" value={amount.toLocaleString("en-IN")}
              onChange={(e) => setAmount(Math.min(Number(e.target.value.replace(/\D/g, "")) || 0, MAX))}
              onBlur={() => setAmount((a) => Math.max(a, MIN))}
              className="flex-1 bg-transparent outline-none text-xl font-bold ml-1" />
          </div>
          <input data-testid="le-emi-slider" type="range" min={MIN} max={MAX} step={10000} value={Math.min(Math.max(amount, MIN), MAX)}
            onChange={(e) => setAmount(Number(e.target.value))} className="le-range mt-5 w-full"
            style={{ background: `linear-gradient(90deg,#0FA3A0 ${pct}%,#E8EEF5 ${pct}%)` }} />
          <div className="mt-2 flex justify-between text-sm text-[#4B5B7C]"><span>₹50,000</span><span>₹50,00,000</span></div>
          <label className="block mt-7 font-semibold">Interest Rate (p.a.)</label>
          <div className="mt-2"><Select testid="le-emi-rate" value={rate} onChange={setRate}>{RATES.map((r) => <option key={r} value={r}>{r}%</option>)}</Select></div>
          <label className="block mt-6 font-semibold">Tenure</label>
          <div className="mt-2"><Select testid="le-emi-tenure" value={tenure} onChange={setTenure}>{TENURES.map((t) => <option key={t} value={t}>{t} Months</option>)}</Select></div>
          <GradButton data-testid="le-emi-calculate" onClick={calculate} className="mt-8 w-full h-14 text-lg">Calculate EMI <ArrowRight className="w-5 h-5" /></GradButton>
        </div>
        <div className="rounded-3xl border border-slate-100 overflow-hidden bg-white">
          <div className="bg-[#E8F8F1] px-8 md:px-12 py-10">
            <div className="text-2xl font-bold">Your EMI</div>
            <div className="mt-3 flex items-baseline gap-3">
              <span data-testid="le-emi-result" className="text-5xl md:text-6xl font-extrabold tracking-tight">{fmtINR(res.emi)}</span>
              <span className="text-2xl">/month</span>
            </div>
          </div>
          <div className="px-8 md:px-12 py-4">
            <SummaryRow label="Loan Amount" value={fmtINR(inputs.amount)} id="principal" />
            <SummaryRow label="Total Interest" value={fmtINR(res.interest)} id="interest" />
            <SummaryRow label="Total Repayment" value={fmtINR(res.total)} id="total" />
          </div>
        </div>
      </div>
    </section>
  );
}
